import { useEffect, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Lock, CheckCircle2, CreditCard } from "lucide-react";
import { stripePromise } from "@/lib/stripe";
import { api } from "@/lib/api";

const SaveCardForm = ({ bookingId, onDone }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError("");
    try {
      const { error: stripeErr, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });
      if (stripeErr) {
        setError(stripeErr.message || "Card could not be saved");
        return;
      }
      await api.post("/public/card-confirm", {
        booking_id: bookingId,
        payment_method_id: setupIntent.payment_method,
      });
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't save the card — we'll handle it when we call.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <PaymentElement />
      {error && <p data-testid="save-card-error" className="mt-3 text-sm font-semibold text-rose-500">{error}</p>}
      <button
        data-testid="save-card-submit"
        type="submit"
        disabled={saving || !stripe}
        className="mt-4 w-full rounded-xl bg-brand-blue px-5 py-3.5 font-display text-sm font-extrabold uppercase tracking-widest text-white transition-transform hover:scale-[1.02] disabled:opacity-50"
      >
        {saving ? "Saving securely…" : "Save my card"}
      </button>
    </form>
  );
};

export const SaveCardSection = ({ bookingId }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [hasCard, setHasCard] = useState(false);
  const [done, setDone] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api
      .post("/public/card-setup", { booking_id: bookingId })
      .then((r) => {
        setHasCard(!!r.data.has_card);
        setClientSecret(r.data.client_secret);
      })
      .catch(() => setClientSecret("error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  if (hasCard) {
    return (
      <div data-testid="save-card-onfile" className="mt-6 flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4">
        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
        <p className="text-sm font-bold text-emerald-700">Card on file — you&apos;re all set. We&apos;ll charge it when your order is scheduled for drop-off.</p>
      </div>
    );
  }

  if (done) {
    return (
      <div data-testid="save-card-success" className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center">
        <CheckCircle2 className="mx-auto h-9 w-9 text-emerald-500" />
        <p className="mt-2 font-display text-base font-extrabold text-brand-navy">Card saved!</p>
        <p className="text-sm text-slate-600">We&apos;ll charge it when your order is on its way back.</p>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        data-testid="save-card-open"
        onClick={() => setOpen(true)}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-brand-blue/40 bg-brand-blue-light/40 px-5 py-4 text-sm font-bold text-brand-blue transition-colors hover:border-brand-blue hover:bg-brand-blue-light"
      >
        <CreditCard className="h-5 w-5" /> Save a card for payment (optional)
      </button>
    );
  }

  return (
    <div data-testid="save-card-section" className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm">
      <p className="font-display text-base font-extrabold text-brand-navy">Save your card for payment</p>
      <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
        <Lock className="h-3.5 w-3.5 text-emerald-600" /> Secured by Stripe — we never see your card number
      </p>
      <div className="mt-4">
        {clientSecret && clientSecret !== "error" ? (
          <Elements stripe={stripePromise} options={{ clientSecret, paymentMethodCreation: "manual" }}>
            <SaveCardForm bookingId={bookingId} onDone={() => setDone(true)} />
          </Elements>
        ) : clientSecret === "error" ? (
          <p className="text-sm font-semibold text-rose-500">Card form unavailable right now — we&apos;ll sort payment when we call.</p>
        ) : (
          <p className="py-6 text-center text-sm font-semibold text-slate-400">Loading secure form…</p>
        )}
      </div>
    </div>
  );
};
